import { useCallback } from "react";
import { Plus, Trash2, GripVertical } from "lucide-react";

/**
 * ProductModifierEditor — inline editor for modifier groups + paid add-ons.
 * Lives inside the dashboard product form. Emits a `{modifier_groups, add_ons}`
 * shape ready to POST/PUT to `/api/businesses/{id}/products`.
 *
 * Kept intentionally flat / low-chrome so it slots into the existing
 * dialog without pushing the Add-Product form off the screen on mobile.
 */
const rid = () => Math.random().toString(36).slice(2, 8);

export default function ProductModifierEditor({ modifierGroups = [], addOns = [], onChange }) {
  const emit = useCallback((mg, ao) => {
    onChange?.({ modifier_groups: mg, add_ons: ao });
  }, [onChange]);

  const addGroup = () => {
    emit([
      ...modifierGroups,
      {
        id: `grp_${rid()}`, name: "New option group",
        required: false, min_select: 0, max_select: 1, options: [],
      },
    ], addOns);
  };
  const patchGroup = (idx, patch) => {
    const next = modifierGroups.map((g, i) => i === idx ? { ...g, ...patch } : g);
    emit(next, addOns);
  };
  const removeGroup = (idx) => emit(modifierGroups.filter((_, i) => i !== idx), addOns);

  const addOption = (gi) => {
    const g = modifierGroups[gi];
    patchGroup(gi, { options: [...(g.options || []), { id: `opt_${rid()}`, name: "", price_delta: 0 }] });
  };
  const patchOption = (gi, oi, patch) => {
    const g = modifierGroups[gi];
    const next = (g.options || []).map((o, i) => i === oi ? { ...o, ...patch } : o);
    patchGroup(gi, { options: next });
  };
  const removeOption = (gi, oi) => {
    const g = modifierGroups[gi];
    patchGroup(gi, { options: (g.options || []).filter((_, i) => i !== oi) });
  };

  const addAddon = () => emit(modifierGroups, [
    ...addOns, { id: `ao_${rid()}`, name: "", price: 0 },
  ]);
  const patchAddon = (idx, patch) => {
    const next = addOns.map((a, i) => i === idx ? { ...a, ...patch } : a);
    emit(modifierGroups, next);
  };
  const removeAddon = (idx) => emit(modifierGroups, addOns.filter((_, i) => i !== idx));

  return (
    <div className="space-y-4" data-testid="modifier-editor">
      {/* Groups */}
      <div className="rounded-xl border border-gray-100 bg-tbc-cream/30 p-3 sm:p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-heading text-sm text-tbc-navy">Option groups</p>
            <p className="text-[11px] text-gray-500 font-body">e.g. "Choose 2 sides", "Pick a size"</p>
          </div>
          <button
            type="button"
            onClick={addGroup}
            className="inline-flex items-center gap-1 rounded-full bg-tbc-navy text-white px-3 py-1 text-xs font-semibold"
            data-testid="add-modifier-group"
          >
            <Plus className="w-3 h-3" /> Add group
          </button>
        </div>

        {modifierGroups.length === 0 ? (
          <p className="mt-3 text-xs text-gray-400 font-body italic">No option groups yet.</p>
        ) : (
          <div className="mt-3 space-y-3">
            {modifierGroups.map((g, gi) => (
              <div key={g.id} className="rounded-lg border border-gray-200 bg-white p-3" data-testid={`mod-group-${gi}`}>
                <div className="flex items-start gap-2">
                  <GripVertical className="w-4 h-4 text-gray-300 mt-1 flex-shrink-0" />
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-2 items-center">
                    <input
                      type="text"
                      placeholder="Group name"
                      value={g.name || ""}
                      onChange={(e) => patchGroup(gi, { name: e.target.value })}
                      className="h-9 rounded-lg border border-gray-200 px-2 text-sm font-body"
                      data-testid={`mod-group-name-${gi}`}
                    />
                    <label className="text-xs font-body text-tbc-navy inline-flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={!!g.required}
                        onChange={(e) => patchGroup(gi, { required: e.target.checked })}
                        className="w-4 h-4 accent-tbc-gold"
                        data-testid={`mod-group-required-${gi}`}
                      />
                      Required
                    </label>
                    <label className="text-xs font-body text-tbc-navy inline-flex items-center gap-1">
                      min
                      <input
                        type="number"
                        min={0}
                        value={g.min_select || 0}
                        onChange={(e) => patchGroup(gi, { min_select: Number(e.target.value) || 0 })}
                        className="w-14 h-9 rounded-lg border border-gray-200 px-2 text-sm font-body"
                      />
                    </label>
                    <label className="text-xs font-body text-tbc-navy inline-flex items-center gap-1">
                      max
                      <input
                        type="number"
                        min={1}
                        value={g.max_select || 1}
                        onChange={(e) => patchGroup(gi, { max_select: Math.max(1, Number(e.target.value) || 1) })}
                        className="w-14 h-9 rounded-lg border border-gray-200 px-2 text-sm font-body"
                      />
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeGroup(gi)}
                    aria-label="Remove group"
                    className="text-red-400 hover:text-red-600 p-1"
                    data-testid={`mod-group-remove-${gi}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Options */}
                <div className="mt-3 pl-6 space-y-1.5">
                  {(g.options || []).map((o, oi) => (
                    <div key={o.id} className="flex items-center gap-2">
                      <input
                        type="text"
                        placeholder="Option name (e.g. Mac and Cheese)"
                        value={o.name || ""}
                        onChange={(e) => patchOption(gi, oi, { name: e.target.value })}
                        className="flex-1 h-8 rounded-lg border border-gray-200 px-2 text-sm font-body"
                        data-testid={`mod-option-name-${gi}-${oi}`}
                      />
                      <div className="flex items-center gap-1">
                        <span className="text-gray-400 text-xs">+$</span>
                        <input
                          type="number"
                          step="0.01"
                          min={0}
                          value={o.price_delta || 0}
                          onChange={(e) => patchOption(gi, oi, { price_delta: Number(e.target.value) || 0 })}
                          className="w-20 h-8 rounded-lg border border-gray-200 px-2 text-sm font-body"
                          data-testid={`mod-option-price-${gi}-${oi}`}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeOption(gi, oi)}
                        aria-label="Remove option"
                        className="text-red-400 hover:text-red-600 p-1"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addOption(gi)}
                    className="inline-flex items-center gap-1 text-xs text-tbc-navy hover:text-tbc-gold font-semibold"
                    data-testid={`add-option-${gi}`}
                  >
                    <Plus className="w-3 h-3" /> Add option
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add-ons */}
      <div className="rounded-xl border border-gray-100 bg-tbc-cream/30 p-3 sm:p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-heading text-sm text-tbc-navy">Paid add-ons</p>
            <p className="text-[11px] text-gray-500 font-body">e.g. "Extra Cornbread +$2.50"</p>
          </div>
          <button
            type="button"
            onClick={addAddon}
            className="inline-flex items-center gap-1 rounded-full bg-tbc-navy text-white px-3 py-1 text-xs font-semibold"
            data-testid="add-addon"
          >
            <Plus className="w-3 h-3" /> Add extra
          </button>
        </div>
        {addOns.length === 0 ? (
          <p className="mt-3 text-xs text-gray-400 font-body italic">No paid add-ons yet.</p>
        ) : (
          <div className="mt-3 space-y-1.5">
            {addOns.map((a, ai) => (
              <div key={a.id} className="flex items-center gap-2" data-testid={`addon-row-${ai}`}>
                <input
                  type="text"
                  placeholder="Extra name"
                  value={a.name || ""}
                  onChange={(e) => patchAddon(ai, { name: e.target.value })}
                  className="flex-1 h-8 rounded-lg border border-gray-200 px-2 text-sm font-body"
                />
                <div className="flex items-center gap-1">
                  <span className="text-gray-400 text-xs">+$</span>
                  <input
                    type="number"
                    step="0.01"
                    min={0}
                    value={a.price || 0}
                    onChange={(e) => patchAddon(ai, { price: Number(e.target.value) || 0 })}
                    className="w-20 h-8 rounded-lg border border-gray-200 px-2 text-sm font-body"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeAddon(ai)}
                  aria-label="Remove extra"
                  className="text-red-400 hover:text-red-600 p-1"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
